/**
 * Discovery / Section 9 - DPDPA minor-data compliance.
 * Sources:
 *   • /v1/discovery/section-9-report  → totals + per-system + per-BU breakdown
 *   • /v1/discovery/findings           → recent §9-related enforcement events
 *
 * Server response shape verified against
 * apps/api-controller/src/modules/discovery/service.ts:generateSection9Report
 * (the prior assumed shape did NOT match - it crashed the page until
 * the type was corrected as part of the Phase-2-part-2 hardening pass).
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { ShieldAlert, ShieldCheck, Users, FileSearch, AlertTriangle, UserPlus, Loader2 } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/primitives';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import { PageGuide } from '@/components/ui/page-guide';
import { FocttaCoreBadge } from '@/components/foctta-core-badge';
import {
  getSection9Report,
  listDiscoveryFindings,
  listParentalConsents,
  recordParentalConsent,
  updateParentalConsent,
  type Section9Report,
  type DiscoveryFinding,
  type ParentalConsentRecord,
  type ParentalConsentSummary,
  type ParentalConsentVerificationMethod,
} from '@/lib/api';

const CONSENT_METHOD_LABELS: Record<ParentalConsentVerificationMethod, string> = {
  digilocker: 'DigiLocker token',
  id_document: 'ID document',
  existing_kyc: 'Existing KYC',
  manual: 'Manual',
};

const CONSENT_STATUS_VARIANT: Record<
  ParentalConsentRecord['status'],
  'success' | 'warning' | 'critical'
> = {
  verified: 'success',
  pending: 'warning',
  revoked: 'critical',
};

interface PageState {
  report: Section9Report | null;
  events: DiscoveryFinding[];
  loading: boolean;
  error: string | null;
}

export function DiscoverySection9() {
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>({
    report: null,
    events: [],
    loading: true,
    error: null,
  });

  const refresh = (signal?: AbortSignal) =>
    Promise.allSettled([
      getSection9Report(),
      listDiscoveryFindings({ limit: 200 }),
    ]).then(([reportRes, findingsRes]) => {
      if (signal?.aborted) return;
      const report = reportRes.status === 'fulfilled' ? reportRes.value : null;
      const allFindings = findingsRes.status === 'fulfilled' ? findingsRes.value.findings : [];
      const events = allFindings
        .filter((f) => {
          const fType = (f.findingType ?? '').toLowerCase();
          return (
            fType.includes('section_9') ||
            fType.includes('minor') ||
            f.classifications.some((c) => /minor|guardian|child/i.test(c.label))
          );
        })
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 8);

      const errors: string[] = [];
      if (reportRes.status === 'rejected') {
        errors.push(`report: ${(reportRes.reason as Error)?.message ?? 'failed'}`);
      }
      if (findingsRes.status === 'rejected') {
        errors.push(`events: ${(findingsRes.reason as Error)?.message ?? 'failed'}`);
      }
      setState({
        report,
        events,
        loading: false,
        error: errors.length > 0 ? errors.join(' · ') : null,
      });
    });

  // ── Verifiable parental consent (DPDPA Section 9) ──
  // Independent of the report generator: the consent workflow is usable even
  // when the report call fails, so it has its own load/error state.
  const [consents, setConsents] = useState<ParentalConsentRecord[]>([]);
  const [consentSummary, setConsentSummary] = useState<ParentalConsentSummary>({
    verified: 0,
    pending: 0,
    revoked: 0,
  });
  const [consentError, setConsentError] = useState<string | null>(null);
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const [formMinorRef, setFormMinorRef] = useState('');
  const [formGuardian, setFormGuardian] = useState('');
  const [formMethod, setFormMethod] = useState<ParentalConsentVerificationMethod>('digilocker');
  const [formExpiry, setFormExpiry] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const refreshConsents = (signal?: AbortSignal) =>
    listParentalConsents()
      .then((res) => {
        if (signal?.aborted) return;
        setConsents(res.records);
        setConsentSummary(res.summary);
        setConsentError(null);
      })
      .catch((e) => {
        if (signal?.aborted) return;
        setConsentError(e instanceof Error ? e.message : 'failed to load consent records');
      });

  function resetConsentForm() {
    setFormMinorRef('');
    setFormGuardian('');
    setFormMethod('digilocker');
    setFormExpiry('');
    setFormError(null);
  }

  async function handleSubmitConsent(): Promise<void> {
    setSubmitting(true);
    setFormError(null);
    try {
      await recordParentalConsent({
        minorRef: formMinorRef.trim(),
        guardianName: formGuardian.trim() || undefined,
        verificationMethod: formMethod,
        expiresAt: formExpiry ? new Date(formExpiry).toISOString() : undefined,
      });
      setConsentDialogOpen(false);
      resetConsentForm();
      await refreshConsents();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'failed to record consent');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRevokeConsent(id: string): Promise<void> {
    try {
      await updateParentalConsent(id, { status: 'revoked' });
      await refreshConsents();
    } catch (e) {
      setConsentError(e instanceof Error ? e.message : 'failed to revoke consent');
    }
  }

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    refreshConsents(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  // Defensive defaults: every nested array is treated as possibly-undefined
  // (the prior code's '.length' on a potentially-undefined field crashed
  // the page; the server's response shape evolves and we render gracefully
  // when fields are missing).
  const bySystem = state.report?.bySystem ?? [];
  const byBusinessUnit = state.report?.byBusinessUnit ?? [];
  const byPack = state.report?.byPack ?? [];
  const totalMinors = state.report?.totalMinorRecordsDetected ?? 0;
  const open = state.report?.openFindings ?? 0;
  const remediated = state.report?.remediatedFindings ?? 0;
  const totalKnown = open + remediated;
  const remediationRatio = totalKnown > 0 ? remediated / totalKnown : 0;

  return (
    <PageContainer>
      <PageHeader
        title="Children's data (DPDPA Section 9)"
        subtitle="Children + processing without verifiable parental consent triggers a hard-block at runtime. ₹200 Cr exposure per violation."
        badges={[
          { label: 'DPDPA Section 9', variant: 'critical' },
          { label: 'India', variant: 'glass' },
          ...(state.loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : state.error
              ? [{ label: 'Degraded', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <div className="flex items-center gap-2">
            <FocttaCoreBadge label="BU names live" />
            <Button size="sm" variant="glass" asChild>
              <Link to="/compliance" title="Generate signed DPB / DPDPA evidence pack">
                Export DPB report
              </Link>
            </Button>
          </div>
        }
      />

      <PageGuide id="section-9">
        DPDPA Section 9 covers children&apos;s personal data, which by law needs verifiable parental
        consent. This page shows whether any children&apos;s data was found in your systems.
        &ldquo;0&rdquo; everywhere means none was detected - which is what you want. If you have not
        run a scan yet, the zeros just mean &ldquo;not checked yet&rdquo;, so run a scan first.
      </PageGuide>

      {state.loading && (
        <LoadingState caption="Loading Section 9 report…" />
      )}

      {state.error && !state.loading && (
        <ErrorState
          title="Some data unavailable"
          detail={state.error}
          onRetry={() => { setState((s) => ({ ...s, error: null, loading: true })); refresh(); }}
        />
      )}

      {!state.loading && state.report && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard
              label="Minor records detected"
              value={totalMinors}
              icon={Users}
              hint={`across ${bySystem.length} systems`}
            />
            <KpiCard
              label="Open findings"
              value={open}
              icon={ShieldAlert}
              glow={open > 0 ? 'critical' : 'medium'}
              hint="Section 9 violations"
            />
            <KpiCard
              label="Remediated"
              value={remediated}
              icon={FileSearch}
              glow={remediated > 0 ? 'high' : 'low'}
              hint="lifecycle complete"
            />
            <KpiCard
              label="Remediation ratio"
              value={remediationRatio}
              format="percent"
              icon={AlertTriangle}
              glow={remediationRatio > 0.8 ? 'high' : remediationRatio > 0.5 ? 'medium' : 'critical'}
              hint={`${totalKnown.toLocaleString('en-IN')} total findings`}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <GlassCard className="p-6">
              <CardHeader className="p-0 mb-3">
                <CardTitle>Per-system breakdown</CardTitle>
                <CardDescription>Where minor-data records live, per source type</CardDescription>
              </CardHeader>
              <div className="space-y-3">
                {bySystem.length === 0 ? (
                  <div className="text-xs text-muted-foreground py-4 text-center">
                    No minor-data records on file in any system.
                  </div>
                ) : (
                  bySystem.map((row, i) => {
                    const ratio = totalMinors > 0 ? row.minorRecordCount / totalMinors : 0;
                    return (
                      <motion.div
                        key={`${row.sourceType}-${i}`}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <code className="text-xs font-mono">{row.sourceType}</code>
                          <span className="text-xs tabular text-muted-foreground">
                            {row.minorRecordCount.toLocaleString('en-IN')} records
                          </span>
                        </div>
                        <div className="h-1.5 bg-muted/40 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${ratio * 100}%` }}
                            transition={{ delay: 0.2 + i * 0.05, duration: 0.8 }}
                            className="h-full bg-warning"
                          />
                        </div>
                      </motion.div>
                    );
                  })
                )}
              </div>
            </GlassCard>

            <GlassCard className="p-6">
              <CardHeader className="p-0 mb-3">
                <CardTitle>Recent enforcement events</CardTitle>
                <CardDescription>
                  Each event = 1 prevented violation; ₹200 Cr exposure averted
                </CardDescription>
              </CardHeader>
              <div className="space-y-2">
                {state.events.length === 0 ? (
                  <div className="text-xs text-muted-foreground py-4 text-center">
                    No Section 9 enforcement events on record.
                  </div>
                ) : (
                  state.events.map((e, i) => (
                    <motion.button
                      type="button"
                      key={e.id}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      onClick={() => navigate({ to: '/dspm/findings', search: { id: e.id } as never })}
                      className="glass-lite p-3 rounded-md flex items-start gap-3 w-full text-left hover:bg-muted/40 transition-colors"
                      title="Open this finding in the Findings table"
                    >
                      <span
                        className={`h-2 w-2 rounded-full mt-1.5 ${
                          e.severity === 'critical' ? 'bg-sev-critical animate-pulse' : 'bg-sev-high'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <code className="font-mono text-[11px] text-primary">{e.id.slice(0, 18)}…</code>
                          <Badge variant={e.severity as 'critical' | 'high' | 'medium' | 'low'} className="text-[9px]">
                            {e.severity}
                          </Badge>
                          {e.status === 'open' && <Badge variant="critical" className="text-[9px]">OPEN</Badge>}
                        </div>
                        <p className="text-sm mt-1 capitalize">
                          {e.findingType.replace(/_/g, ' ')}
                        </p>
                        {e.classifications.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {e.classifications.map((c) => c.label).join(' · ')}
                          </p>
                        )}
                      </div>
                    </motion.button>
                  ))
                )}
              </div>
            </GlassCard>
          </div>

          {byBusinessUnit.length > 0 && (
            <GlassCard className="p-6 mb-6">
              <CardHeader className="p-0 mb-3">
                <CardTitle>Per-business-unit breakdown</CardTitle>
                <CardDescription>Where each BU&rsquo;s minor data lives</CardDescription>
              </CardHeader>
              <div className="space-y-3">
                {byBusinessUnit.map((row, i) => (
                  <motion.div
                    key={`${row.businessUnitId ?? 'unassigned'}-${i}`}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <div className="flex items-center justify-between">
                      <code className="text-xs font-mono">
                        {row.businessUnitId ?? <span className="text-muted-foreground italic">unassigned</span>}
                      </code>
                      <span className="text-xs tabular text-muted-foreground">
                        {row.minorRecordCount.toLocaleString('en-IN')} records
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </GlassCard>
          )}

          {byPack.length > 0 && (
            <GlassCard className="p-6 mb-6">
              <CardHeader className="p-0 mb-3">
                <CardTitle>Per-industry-pack coverage</CardTitle>
                <CardDescription>
                  How much of your minor-data exposure is covered by each activated pack&rsquo;s vocabulary.
                  Green ≥ 80% · Amber ≥ 40% · Red &lt; 40%.
                </CardDescription>
              </CardHeader>
              <div className="space-y-3">
                {byPack.map((row, i) => {
                  // 'unknown' means the coverage ratio is undefined (0/0 - no
                  // minor records detected), not that coverage is zero. Both
                  // chains below used to fall through to red for anything that
                  // was not green or amber, so an undefined ratio rendered as a
                  // critical red bar at 0%. That is the same overstatement the
                  // backend was just fixed for, and it must not come back in
                  // through the view.
                  const unknownCoverage = row.coverageState === 'unknown';
                  const pct = Math.round(row.coverageScore * 100);
                  const barClass =
                    row.coverageState === 'green'
                      ? 'bg-success'
                      : row.coverageState === 'amber'
                        ? 'bg-warning'
                        : unknownCoverage
                          ? 'bg-muted-foreground/40'
                          : 'bg-sev-critical';
                  const badgeVariant =
                    row.coverageState === 'green'
                      ? 'success'
                      : row.coverageState === 'amber'
                        ? 'warning'
                        : unknownCoverage
                          ? 'glass'
                          : 'critical';
                  return (
                    <motion.div
                      key={row.packId}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                    >
                      <div className="flex items-center justify-between mb-1 gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-medium truncate">{row.packLabel}</span>
                          <code className="text-[10px] font-mono text-muted-foreground">{row.packId}</code>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs tabular text-muted-foreground">
                            {row.assetCount.toLocaleString('en-IN')} assets
                          </span>
                          <Badge variant={badgeVariant} className="text-[10px] tabular">
                            {unknownCoverage ? 'n/a' : `${pct}%`}
                          </Badge>
                        </div>
                      </div>
                      <div className="h-1.5 bg-muted/40 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ delay: 0.2 + i * 0.05, duration: 0.8 }}
                          className={`h-full ${barClass}`}
                        />
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </GlassCard>
          )}

          <GlassCard className="p-6">
            <CardHeader className="p-0 mb-2">
              <CardTitle>Regulations in scope</CardTitle>
              <CardDescription>
                This report applies to the following regulatory regimes.
              </CardDescription>
            </CardHeader>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {(state.report.regulations ?? []).map((r) => (
                <Badge key={r} variant="glass" className="text-[10px] font-mono">{r}</Badge>
              ))}
            </div>
            <div className="text-xs text-muted-foreground">
              Last refresh:{' '}
              <code className="font-mono text-foreground/80">
                {new Date(state.report.reportGeneratedAt).toLocaleString()}
              </code>
            </div>
          </GlassCard>
        </>
      )}

      {!state.loading && !state.report && (
        <div className="glass-lite rounded-md p-8 text-center text-sm text-muted-foreground">
          <ShieldAlert className="h-8 w-8 mx-auto mb-3 opacity-40" />
          <div className="font-medium text-foreground/90">No Section 9 report available</div>
          <div className="mt-1">
            The report generator runs on demand; check connector health if this persists.
          </div>
        </div>
      )}

      {/* ── Verifiable parental consent (DPDPA Section 9) ─────────────────
          Independent of the report generator - always available so the
          workflow works even when the report call is degraded. */}
      {!state.loading && (
        <div className="mt-6 space-y-4">
          <div className="glass-lite rounded-md p-4 border border-sev-critical/40 flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-sev-critical shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-medium text-foreground/90">Why this matters - DPDPA Section 9</div>
              <p className="text-xs text-muted-foreground mt-1 leading-snug">
                Verifiable parental consent is mandatory before a Data Fiduciary may process a
                child&apos;s personal data. Consent must be verified either against details you
                reliably hold (existing KYC) or a government-authorised virtual token (DigiLocker).
                Non-compliance carries penalties of up to Rs 200 crore.
              </p>
            </div>
          </div>

          <GlassCard className="p-6">
            <CardHeader className="p-0 mb-4 flex-row items-start justify-between gap-3">
              <div>
                <CardTitle>Verifiable parental consent (DPDPA Section 9)</CardTitle>
                <CardDescription>
                  Record and track guardian consent for detected minor records.
                </CardDescription>
              </div>
              <Button size="sm" onClick={() => { resetConsentForm(); setConsentDialogOpen(true); }}>
                <UserPlus className="h-3.5 w-3.5 mr-1" /> Record parental consent
              </Button>
            </CardHeader>

            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="glass-lite rounded-md p-3 text-center">
                <div className="text-2xl font-semibold text-success tabular">
                  {consentSummary.verified.toLocaleString('en-IN')}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Verified</div>
              </div>
              <div className="glass-lite rounded-md p-3 text-center">
                <div className="text-2xl font-semibold text-warning tabular">
                  {consentSummary.pending.toLocaleString('en-IN')}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Pending</div>
              </div>
              <div className="glass-lite rounded-md p-3 text-center">
                <div className="text-2xl font-semibold text-sev-critical tabular">
                  {consentSummary.revoked.toLocaleString('en-IN')}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Revoked</div>
              </div>
            </div>

            {consentError && (
              <div className="glass-lite rounded-md p-2 mb-3 text-[11px] text-destructive border border-destructive/40">
                {consentError}
              </div>
            )}

            {consents.length === 0 ? (
              <div className="text-xs text-muted-foreground py-4 text-center">
                No parental-consent records yet. Record verifiable consent for each detected minor.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
                      <th className="py-2 pr-3 font-medium">Minor ref</th>
                      <th className="py-2 pr-3 font-medium">Guardian</th>
                      <th className="py-2 pr-3 font-medium">Method</th>
                      <th className="py-2 pr-3 font-medium">Status</th>
                      <th className="py-2 pr-3 font-medium">Verified</th>
                      <th className="py-2 pr-3 font-medium">Receipt</th>
                      <th className="py-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consents.map((c) => (
                      <tr key={c.id} className="border-b border-border/20">
                        <td className="py-2 pr-3 font-mono">{c.minorRef}</td>
                        <td className="py-2 pr-3">{c.guardianName ?? '-'}</td>
                        <td className="py-2 pr-3">
                          <Badge variant="glass" className="text-[9px]">
                            {CONSENT_METHOD_LABELS[c.verificationMethod]}
                          </Badge>
                        </td>
                        <td className="py-2 pr-3">
                          <Badge variant={CONSENT_STATUS_VARIANT[c.status]} className="text-[9px]">
                            {c.status}
                          </Badge>
                        </td>
                        <td className="py-2 pr-3 text-muted-foreground tabular">
                          {c.verifiedAt ? new Date(c.verifiedAt).toLocaleDateString('en-IN') : '-'}
                        </td>
                        <td className="py-2 pr-3">
                          <code className="text-[10px] text-muted-foreground" title={c.receiptHash ?? undefined}>
                            {c.receiptHash ? `${c.receiptHash.slice(0, 10)}…` : '-'}
                          </code>
                        </td>
                        <td className="py-2">
                          {c.status !== 'revoked' && (
                            <button
                              type="button"
                              onClick={() => void handleRevokeConsent(c.id)}
                              className="text-[10px] text-sev-critical hover:underline"
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </GlassCard>
        </div>
      )}

      <Dialog
        open={consentDialogOpen}
        onOpenChange={(o) => { if (!o) resetConsentForm(); setConsentDialogOpen(o); }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Record parental consent</DialogTitle>
            <DialogDescription>
              DPDPA Section 9 - capture verifiable guardian consent for a detected minor record.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-xs">
            <div>
              <label className="text-muted-foreground block mb-0.5">Minor reference</label>
              <input
                type="text"
                value={formMinorRef}
                onChange={(e) => setFormMinorRef(e.target.value)}
                placeholder="e.g. KYC-MINOR-88213"
                className="glass-lite w-full px-3 py-1.5 rounded-md text-xs border border-border/40 bg-background/40 focus:outline-none focus:border-primary font-mono"
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Use an opaque/hashed handle - never plaintext PII.
              </p>
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Guardian name</label>
              <input
                type="text"
                value={formGuardian}
                onChange={(e) => setFormGuardian(e.target.value)}
                placeholder="e.g. Ramesh Iyer"
                className="glass-lite w-full px-3 py-1.5 rounded-md text-xs border border-border/40 bg-background/40 focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Verification method</label>
              <select
                value={formMethod}
                onChange={(e) => setFormMethod(e.target.value as ParentalConsentVerificationMethod)}
                className="glass-lite w-full px-3 py-1.5 rounded-md text-xs border border-border/40 bg-background/40 focus:outline-none focus:border-primary"
              >
                <option value="digilocker">DigiLocker virtual token</option>
                <option value="id_document">ID document</option>
                <option value="existing_kyc">Existing KYC on file</option>
                <option value="manual">Manual</option>
              </select>
              {formMethod === 'digilocker' && (
                <p className="text-[10px] text-primary mt-1 leading-snug">
                  Uses a government-authorised virtual token (the India-native method DPDPA permits)
                  to verify the guardian&apos;s identity.
                </p>
              )}
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Expiry (optional)</label>
              <input
                type="date"
                value={formExpiry}
                onChange={(e) => setFormExpiry(e.target.value)}
                className="glass-lite w-full px-3 py-1.5 rounded-md text-xs border border-border/40 bg-background/40 focus:outline-none focus:border-primary"
              />
            </div>
            {formError && (
              <div className="glass-lite rounded-md p-2 text-[11px] text-destructive border border-destructive/40">
                {formError}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="glass" size="sm" onClick={() => setConsentDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => void handleSubmitConsent()}
              disabled={submitting || !formMinorRef.trim()}
            >
              {submitting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              {submitting ? 'Recording…' : 'Record consent'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
